import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { openDeleteModal } from "@components/DeleteModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { consultationServiceStatusColors } from "@constants/colors";
import { QP } from "@constants/index";

import { useDeleteClinicConsultationService } from "@hooks/mutations/clinic/useDeleteClinicConsultationService";
import { useGetClinicConsultationServicesDetailed } from "@hooks/queries/clinic/useGetClinicConsultationServicesDetailed";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { ClinicConsultationService } from "@customTypes/clinic";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddConsultationServiceModal } from "./components/AddConsultationServiceModal";
import { EditConsultationServiceModal } from "./components/EditConsultationServiceModal";


export const ConsultationTab = () => {
  const { t, i18n } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });

  const [tableData, setTableData] = useState<ClinicConsultationService[]>([]);
  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [currentConsultationService, setCurrentConsultationService] =
    useState<ClinicConsultationService | null>(null);

  const [
    addConsultationServiceModalOpened,
    { open: openAddConsultationServiceModal, close: closeAddConsultationServiceModal },
  ] = useDisclosure(false);

  const [
    editConsultationServiceModalOpened,
    { open: openEditConsultationServiceModal, close: closeEditConsultationServiceModal },
  ] = useDisclosure(false);

  const { data, isLoading, isRefetching, fetchStatus, isError, isSuccess } = useGetClinicConsultationServicesDetailed({
    pagination: {
      page: tablePagination.page,
      pageSize: tablePagination.pageSize,
    },
    search: debounced.toLowerCase(),
  });

  const { data: partnersData } = useGetPartners();

  const partnersMap = useMemo(() => {
    const map: Record<string, string> = {};
    (partnersData || []).forEach(p => {
      map[p._id] = p.name;
    });
    return map;
  }, [partnersData]);

  const {
    mutate: deleteConsultationService,
    isLoading: isDeleteConsultationServiceLoading,
  } = useDeleteClinicConsultationService();


  // Consultation service table rows (AdminDashboard pattern)
  const consultationServiceRows = useMemo(() => {
    return tableData?.map(service => (
      <Table.Tr key={service.id}>
        <Table.Td>
          {(() => {
            const value = service.name || "-";
            const isLong = value && value.length > 20;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={500}
                  lh={1.5}
                  style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {service.partner_id ? partnersMap[service.partner_id] || "-" : "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {service.category || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {service.price || 0}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge
            fz={12}
            c={consultationServiceStatusColors.get(service.status as string)?.c}
            bg={consultationServiceStatusColors.get(service.status as string)?.bg}>
            {t(service.status || "active")}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="resources.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentConsultationService(service);
                  openEditConsultationServiceModal();
                }}
              >
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="resources.delete"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={true}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => {
                  openDeleteModal({
                    title: t("delete-consultation-service"),
                    textNode: (
                      <Text size="sm" lh={1.5}>
                        <Trans
                          i18nKey="delete-consultation-service-confirmation"
                          values={{ serviceName: service.name }}
                          components={{ bold: <Text span fw={700} /> }}
                        />
                      </Text>
                    ),
                    confirmText: t("delete-service"),
                    onConfirm: () => {
                      deleteConsultationService(service.id);
                    },
                  });
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [tableData, i18n.language, consultationServiceStatusColors]);



  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data, setTablePagination]);

  useEffect(() => {
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length && !isMounted}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        />

        <div className="header">
          <Group w={"100%"} justify="space-between" align="center">
            <h1>Consultations</h1>
            <PermissionGate permission="resources.create"><Button
              leftSection={<IconPlus size={16} />}
              onClick={openAddConsultationServiceModal}>
              {t("add-consultation-service")}
            </Button></PermissionGate>
          </Group>
        </div>

        <TextInput
          my={"md"}
          placeholder={t("search-for-a-consultation-service")}
          leftSection={<IconSearch size={16} />}
          value={searchFilter || ""}
          onChange={event => setSearchFilter(event.currentTarget.value || null)}
          style={{ flex: 1, maxWidth: 400 }}
        />

        <div style={{paddingBottom: "200px"}}>
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
            <Table
              ref={tableRef}
              withRowBorders={true}
              mt={"1rem"}
              miw={1280}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
            <Table.Thead>
              <Table.Tr bg={"var(--clr-gray-100)"}>
                <Table.Th>{t("name")}</Table.Th>
                <Table.Th>{t("partner")}</Table.Th>
                <Table.Th>{t("category")}</Table.Th>
                <Table.Th>{t("price")}</Table.Th>
                <Table.Th>{t("status")}</Table.Th>
                <Table.Th>{t("action")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {renderLoadingErrorData({
                isLoading: isLoading && fetchStatus === "fetching",
                isError,
                isSuccess,
                dataLength: tableData.length,
                errorComponent: (
                  <TableErrorRow
                    colSpan={6}
                    message={t("encountered-error")}
                  />
                ),
                loadingComponent: (
                  <TableSkeletonRows
                    columns={6}
                    skeletonProps={{
                      widths: ["lg", "md", "md", "md", "md", "sm"],
                    }}
                  />
                ),
                emptyDataComponent: (
                  <TableEmptyRow
                    colSpan={6}
                    message={
                      debounced
                        ? t("no-consultation-services-found")
                        : t("no-consultation-services-available")
                    }
                  />
                ),
                dataComponent: consultationServiceRows,
              })}
            </Table.Tbody>
            </Table>

            {/* Consultation Services Pagination */}
            {data && !(isLoading && fetchStatus === "fetching") && (
              <TablePagination
                paginationData={data?.pagination || {}}
                paginatedRows={data?.newData?.length || 0}
                onPageChange={(page) => setTablePagination(prev => ({ ...prev, page }))}
                isLoading={isLoading || fetchStatus === "fetching"}
              />
            )}
          </ScrollArea>
        </div>

        <AddConsultationServiceModal
          opened={addConsultationServiceModalOpened}
          close={closeAddConsultationServiceModal}
        />

        <EditConsultationServiceModal
          opened={editConsultationServiceModalOpened}
          close={closeEditConsultationServiceModal}
          consultationService={currentConsultationService}
        />
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
